import type { StandardBotsRO1State } from '@sb/integrations/implementations/StandardBotsRO1/types';
import type { EquipmentItem } from '@sb/integrations/types/deviceZodTypes';
import { isForceSensorKind } from '@sb/integrations/utils/util';
import { makeNamespacedLog } from '@sb/log';
import {
  FrameType,
  StereoPosition,
} from '@sb/messenger/auto_generated/messenger/camera_bot_service';
import { type EquipmentState } from '@sb/routine-runner';

import { INSV_FILE_TYPE } from './constants';
import type {
  BotHardware,
  EquipmentAssignment,
  EquipmentAssignmentValue,
  HardwareConfigEquipmentMapping,
  HardwareConfigSchema,
  ParsedModel,
} from './types';
import { BotHardwareDataType } from './types';

export { flattenInferenceConfig } from './utils/flattenInferenceConfig';

const log = makeNamespacedLog('libs.ai.helpers');

/**
 * True when a training-data session is a handheld recording (an Insta360
 * capture) rather than a robot recording, detected by the presence of an
 * `.insv` file — unique to Insta360 captures. Handheld sessions have no real
 * robot arm, so the data-review 3D Poses view hides it. Pass the session's
 * `files` array.
 */
export const isHandheldRecording = (
  files: ReadonlyArray<{ type?: string | null }> | null | undefined,
): boolean => (files ?? []).some((file) => file.type === INSV_FILE_TYPE);

// Inlined to avoid a runtime dependency on `@sb/integrations` — api-srv's
// Docker build excludes that lib from its context.
const ACTUATING_GRIPPER_KINDS = new Set<EquipmentItem['config']['kind']>([
  'OnRobot3FG15',
  'OnRobot2FG7',
  'OnRobot2FG14',
  'OnRobotDualQuickChanger',
  'OnRobotScrewdriver',
  'OnRobotVGP20',
  'CustomGripper',
  'SchunkEGx',
  'Robotiq2F',
  'RobotiqEPick',
]);

/**
 * Camera-bot registers this config option only for a device whose hardware can
 * produce depth (see `RealSenseCamera`/`LuxonisCamera`), and its value defaults
 * to false. Its *presence* is therefore a capability marker that survives depth
 * being switched off, which its value is not.
 */
const DEPTH_CAPABILITY_CONFIG_OPTION = 'tof';

/**
 * True for the half of a software-stereo pair that produces the depth stream.
 *
 * A software-stereo rig is two separate Camera equipment items, and both carry
 * `enableStereo` — the position selector only appears once stereo is on. Only
 * the LEFT-positioned one publishes DEPTH though (camera-bot gates the stream
 * on `position == LEFT`), and a GigE stereo camera registers no depth config
 * option, so `enableStereo` alone would qualify the RIGHT half for a depth slot
 * that it can never fill.
 *
 * Tested as "not RIGHT" rather than "is LEFT" so that stereo config with no
 * position recorded stays eligible, matching how the rest of the predicate
 * treats missing information. Only an explicit RIGHT is ruled out.
 */
const isStereoDepthSource = (
  config: Extract<EquipmentItem['config'], { kind: 'Camera' }>,
): boolean =>
  config.enableStereo === true &&
  config.stereoConfig?.position !== StereoPosition.RIGHT;

/**
 * True for a Camera equipment item that can serve a `depth_sensor` hardware
 * slot. A mono camera has no depth to record, so binding one to a depth slot
 * yields a session with no depth in it.
 *
 * The test is deliberately capability, not current enablement. An advertised
 * DEPTH stream in `availableStreams` proves depth is live, but its absence
 * proves nothing: camera-bot only emits that entry once depth is switched on
 * for the device (the `tof` option defaults to false, and software stereo also
 * needs `enableStereo`, a calibration, and the `stereoDepth` flag). Excluding a
 * camera for advertising no DEPTH *yet* would hide the one correct camera from
 * the slot and leave the operator an empty menu — a worse failure than
 * offering a camera that cannot deliver.
 *
 * So a camera qualifies when any of these hold, and only a device with no path
 * to depth at all is filtered out:
 * - it advertises a DEPTH stream (depth is live)
 * - it exposes the depth-capability config option (depth hardware, maybe off)
 * - it is a software-stereo depth source (see `isStereoDepthSource`)
 * - it advertises no streams at all — `availableStreams` is a live camera-bot
 *   observation, empty for a disconnected camera and for one whose publishers
 *   are still coming up, so absence of streams is absence of information
 */
const canProvideDepth = (equipment: EquipmentItem): boolean => {
  if (equipment.config.kind !== 'Camera') {
    return false;
  }

  const { availableStreams, configuration } = equipment.config;
  const streams = availableStreams ?? [];

  return (
    streams.length === 0 ||
    streams.some((stream) => stream.frameType === FrameType.DEPTH) ||
    DEPTH_CAPABILITY_CONFIG_OPTION in (configuration?.configOptions ?? {}) ||
    isStereoDepthSource(equipment.config)
  );
};

export const filterEquipmentForBotHardware = ({
  botHardware,
  equipmentList,
}: {
  botHardware: Omit<BotHardware, 'created' | 'archived'>;
  equipmentList: EquipmentItem[];
}): EquipmentItem[] | null => {
  switch (botHardware.equipmentType) {
    case 'gripper':
      return equipmentList.filter((eq) =>
        ACTUATING_GRIPPER_KINDS.has(eq.config.kind),
      );
    case 'force_sensor':
      return equipmentList.filter((eq) => isForceSensorKind(eq.config.kind));
    case 'camera':
      return equipmentList.filter((eq) => eq.config.kind === 'Camera');
    case 'depth_sensor':
      return equipmentList.filter(canProvideDepth);
    default:
      return null;
  }
};

/**
 * Normalizes a hardware-key assignment value to its object form. Accepts the
 * legacy bare-string shorthand (still persisted for feed-less assignments,
 * see `EquipmentAssignmentValue`) alongside the `{equipmentId, feed?}` object
 * form, and always returns the object form.
 */
export const toEquipmentAssignment = (
  value: EquipmentAssignmentValue,
): EquipmentAssignment => {
  if (typeof value === 'string') {
    return { equipmentId: value };
  }

  return value.feed
    ? { equipmentId: value.equipmentId, feed: value.feed }
    : { equipmentId: value.equipmentId };
};

/**
 * Validates that a robot has all required hardware equipment assigned before running inference.
 * @param robot - The robot's hardware requirements from the model's hardware config
 * @param robotEquipmentMapping - The equipment mapping for this robot
 * @param botHardwareMap - Map of BotHardware definitions required by the model (hardware ID -> BotHardware)
 * @returns true if the robot is missing required equipment assignments, false otherwise
 */
export const validateRobotHardwareMapping = ({
  robot,
  robotEquipmentMapping,
  botHardwareMap,
}: {
  robot: HardwareConfigSchema['robots'][string];
  robotEquipmentMapping: HardwareConfigEquipmentMapping[string];
  botHardwareMap: Record<string, BotHardware> | null | undefined;
}): boolean => {
  // For secondary robots, verify robotEquipmentId is set
  if (!robot.isPrimary && !robotEquipmentMapping.robotEquipmentId) {
    return true;
  }

  const hardwareKeys = Object.keys(robot.hardware || {});

  const hasUnassignedHardware = hardwareKeys.some((hardwareKey) => {
    const rawValue =
      robotEquipmentMapping.hardwareKeyToEquipmentId[hardwareKey];

    const hardwareId = robot.hardware[hardwareKey]?.id;

    // Get the BotHardware definition from the model's required hardware
    const botHardware = hardwareId ? botHardwareMap?.[hardwareId] : null;

    // Pose data type is inferred from the robot, not explicitly assigned
    if (botHardware?.dataType === BotHardwareDataType.enum.pose) {
      return false; // Not unassigned, continue checking other hardware
    }

    if (rawValue == null) {
      return true;
    }

    // Normalize before reading equipmentId — an object-form assignment (a
    // dual-lens feed selection) is always truthy, so reading it unnormalized
    // would treat `{equipmentId: ''}` as "assigned".
    const { equipmentId } = toEquipmentAssignment(rawValue);

    // Empty string means "Assign Equipment" placeholder is selected
    return !equipmentId;
  });

  return hasUnassignedHardware;
};

export const hardwareConfigEquipmentMappingToEquipmentKeyToEquipmentId = (
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping,
): Record<string, EquipmentAssignment> => {
  return Object.values(hardwareConfigEquipmentMapping).reduce(
    (acc, robotMap) => {
      const robotEquipmentMap = Object.entries(
        robotMap.hardwareKeyToEquipmentId,
      ).reduce(
        (innerAcc, [hardwareKey, value]) => {
          return {
            ...innerAcc,
            [hardwareKey]: toEquipmentAssignment(value),
          };
        },
        {} as Record<string, EquipmentAssignment>,
      );

      return {
        ...acc,
        ...robotEquipmentMap,
      };
    },
    {} as Record<string, EquipmentAssignment>,
  );
};

export type TMappingFromEquipmentKeyToEquipmentInfo = Record<
  string,
  EquipmentAssignment & { robotKey: string }
>;

export const createMappingFromHardwareKeyToEquipmentInfoHardwareConfig = (
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping,
): TMappingFromEquipmentKeyToEquipmentInfo => {
  return Object.entries(hardwareConfigEquipmentMapping).reduce(
    (acc, [robotKey, robotMap]) => {
      const robotEquipmentMap = Object.entries(
        robotMap.hardwareKeyToEquipmentId,
      ).reduce((innerAcc, [hardwareKey, value]) => {
        return {
          ...innerAcc,
          [hardwareKey]: { robotKey, ...toEquipmentAssignment(value) },
        };
      }, {} as TMappingFromEquipmentKeyToEquipmentInfo);

      return {
        ...acc,
        ...robotEquipmentMap,
      };
    },
    {} as TMappingFromEquipmentKeyToEquipmentInfo,
  );
};

export const createMappingFromEquipmentKeyToEquipmentInfoSensors = (
  equipmentKeyToEquipmentId: Record<string, EquipmentAssignmentValue>,
) => {
  return Object.entries(equipmentKeyToEquipmentId).reduce(
    (acc, [equipmentKey, value]) => {
      return {
        ...acc,
        [equipmentKey]: {
          robotKey: 'robot_0',
          ...toEquipmentAssignment(value),
        },
      };
    },
    {} as TMappingFromEquipmentKeyToEquipmentInfo,
  );
};

export const createMappingFromHardwareKeyToEquipmentInfo = (
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping,
): TMappingFromEquipmentKeyToEquipmentInfo => {
  return createMappingFromHardwareKeyToEquipmentInfoHardwareConfig(
    hardwareConfigEquipmentMapping,
  );
};

/**
 * Extract hardware keys from hardware configs
 * @param hardwareConfigs Single hardware config or array of hardware configurations
 * @returns Array of unique hardware keys across all configs
 */
export const extractHardwareKeysFromConfig = (
  hardwareConfigs: HardwareConfigSchema | HardwareConfigSchema[],
): string[] => {
  const configs = Array.isArray(hardwareConfigs)
    ? hardwareConfigs
    : [hardwareConfigs];

  return [
    ...new Set(
      configs.flatMap((config) =>
        Object.keys(config.robots).flatMap((robotId) =>
          Object.keys(config.robots[robotId].hardware ?? {}),
        ),
      ),
    ),
  ];
};

/**
 * Architecture input keys that name no hardware and never can.
 *
 * `gt_pose` is a ground-truth channel the processor writes into the Zarr
 * alongside the recorded streams; there is no `bot_hardware` row for it, so no
 * hardware config can contain it. That is what separates it from a key like
 * `pose`, which IS a catalog row and belongs in the task's config — a task
 * declaring `pose` recovered still lists it there, which is why the
 * name-based waiver for it is gone.
 *
 * Left out of the requirement instead, because requiring it is unsatisfiable
 * rather than merely unsatisfied: an architecture asking for it would match no
 * task, and no edit an operator can make to a hardware config would change
 * that. Failing loudly is the right answer only where a fix exists.
 */
export const NON_HARDWARE_ARCHITECTURE_KEYS: readonly string[] = ['gt_pose'];

/** Architecture keys minus the ones no hardware config can ever hold. */
export const excludeNonHardwareKeys = (keys: string[]): string[] =>
  keys.filter((key) => !NON_HARDWARE_ARCHITECTURE_KEYS.includes(key));

/** One `(robotKey, hardwareKey) → bot-hardware id` entry of a hardware config. */
export interface HardwareKeyRef {
  robotKey: string;
  hardwareKey: string;
  /** The `bot_hardware` row the config points at for this key. */
  id: string;
}

export interface PartitionedHardwareKeys {
  /** Keys the rig records — an absent file for one of these is a missing upload. */
  recorded: HardwareKeyRef[];
  /** Keys post-processing recovers — no upload is ever expected. */
  recovered: HardwareKeyRef[];
}

/**
 * Split a hardware config's keys by provenance, using the task's recovered
 * declaration (see `RecoveredHardwareKeysSchema`). Every TS consumer of that
 * declaration goes through here, so the upload gate, the planner oracles and
 * the task-default preview can never disagree about which keys are recorded.
 *
 * A recovered entry naming a robot or key the config does not have is ignored
 * rather than treated as an error: a stale declaration left behind by a config
 * swap must never drop a key the rig really records. Upsert validation is
 * where such an entry is rejected, on the task that owns it.
 *
 * An empty (or absent) declaration puts every key in `recorded`, which is what
 * every task did before the field existed.
 */
export const partitionHardwareKeys = (
  config: HardwareConfigSchema | null | undefined,
  recovered: Record<string, readonly string[]> | null | undefined,
): PartitionedHardwareKeys => {
  const result: PartitionedHardwareKeys = { recorded: [], recovered: [] };

  for (const [robotKey, robot] of Object.entries(config?.robots ?? {})) {
    const recoveredForRobot = new Set(recovered?.[robotKey] ?? []);

    for (const [hardwareKey, { id }] of Object.entries(robot?.hardware ?? {})) {
      const ref = { robotKey, hardwareKey, id };

      if (recoveredForRobot.has(hardwareKey)) {
        result.recovered.push(ref);
      } else {
        result.recorded.push(ref);
      }
    }
  }

  return result;
};

/**
 * Validate that all hardware configs contain the required hardware keys
 * @param requiredKeys Hardware keys that must be present in all configs
 * @param hardwareConfigKeys Array of hardware key arrays to validate
 * @returns Object with validation result and missing keys
 */
export const validateAllHardwareConfigsContainKeys = (
  requiredKeys: string[],
  hardwareConfigKeys: string[][],
): {
  isValid: boolean;
  missingHardwareKeys: string[];
  error?: string;
} => {
  if (requiredKeys.length === 0 || hardwareConfigKeys.length === 0) {
    return {
      isValid: true,
      missingHardwareKeys: [],
    };
  }

  const missingHardwareConfigKeys = requiredKeys.filter(
    (key) => !hardwareConfigKeys.every((keys) => keys.includes(key)),
  );

  if (missingHardwareConfigKeys.length > 0) {
    return {
      isValid: false,
      missingHardwareKeys: missingHardwareConfigKeys,
      error: `Hardware config keys ${missingHardwareConfigKeys.join(', ')} not found in all provided configs`,
    };
  }

  return {
    isValid: true,
    missingHardwareKeys: [],
  };
};

// Validates input modelArchitectures are compatible with each other
export const validateModelArchitecturesCompatibility = (
  modelArchitectures: Array<{
    id: string;
    name: string;
    inputHardwareConfig?: { config: any } | null;
    outputHardwareConfig?: { config: any } | null;
  }>,
): {
  isValid: boolean;
  message?: string;
} => {
  if (modelArchitectures.length === 0) {
    return { isValid: true };
  }

  const validArchitectures = modelArchitectures.filter(
    (arch) =>
      arch.inputHardwareConfig?.config && arch.outputHardwareConfig?.config,
  );

  if (validArchitectures.length === 0) {
    return { isValid: true };
  }

  const allInputKeys = extractHardwareKeysFromConfig(
    validArchitectures.map((arch) => arch.inputHardwareConfig!.config),
  );

  const allOutputKeys = extractHardwareKeysFromConfig(
    validArchitectures.map((arch) => arch.outputHardwareConfig!.config),
  );

  const inputKeysPerArch = validArchitectures.map((arch) =>
    extractHardwareKeysFromConfig(arch.inputHardwareConfig!.config),
  );

  const outputKeysPerArch = validArchitectures.map((arch) =>
    extractHardwareKeysFromConfig(arch.outputHardwareConfig!.config),
  );

  const inputValidation = validateAllHardwareConfigsContainKeys(
    allInputKeys,
    inputKeysPerArch,
  );

  const outputValidation = validateAllHardwareConfigsContainKeys(
    allOutputKeys,
    outputKeysPerArch,
  );

  const isValid = inputValidation.isValid && outputValidation.isValid;

  return {
    isValid,
    message: isValid
      ? undefined
      : 'Selected models have incompatible hardware configurations.',
  };
};

/**
 * Filters training runs/checkpoints to show only those compatible with the selected project and model architecture.
 * Applies three filters:
 * 1. Exact project ID match
 * 2. Compatible model architecture hardware configuration match
 * 3. Must have valid checkpoint data
 *
 * @param allCheckpoints - All available training runs/checkpoints
 * @param selectedProjectId - User-selected project ID that checkpoints must match
 * @param selectedModelArchitecture - User-selected model architecture for hardware compatibility validation
 * @returns Array of compatible checkpoints
 */
export const filterCompatibleCheckpoints = <
  T extends {
    id: string;
    trainingDataProjectId?: string | null;
    modelArchitectureId?: string | null;
    modelArchitecture?: {
      id: string;
      name: string;
      inputHardwareConfig?: { config: any } | null;
      outputHardwareConfig?: { config: any } | null;
    } | null;
    checkpointPath?: string | null;
    epoch?: number | null;
  },
>(
  allCheckpoints: T[],
  selectedProjectId: string,
  selectedModelArchitecture: {
    id: string;
    name: string;
    inputHardwareConfig?: { config: any } | null;
    outputHardwareConfig?: { config: any } | null;
  },
): T[] => {
  return allCheckpoints.filter((checkpoint) => {
    // Filter 1: Exact project ID match
    if (checkpoint.trainingDataProjectId !== selectedProjectId) {
      return false;
    }

    // Filter 2: Must have checkpoint path and epoch (basic checkpoint requirements)
    if (!checkpoint.checkpointPath || !checkpoint.epoch) {
      return false;
    }

    // Filter 3: Compatible model architecture hardware configuration match
    const checkpointModelArch = checkpoint.modelArchitecture;

    if (!checkpointModelArch) {
      // If no model architecture info, fall back to ID-based matching
      return checkpoint.modelArchitectureId === selectedModelArchitecture.id;
    }

    // Use existing validation logic to check hardware config compatibility
    const validation = validateModelArchitecturesCompatibility([
      selectedModelArchitecture,
      checkpointModelArch,
    ]);

    return validation.isValid;
  });
};

/**
 * Filter tasks to show only those compatible with the selected model architectures
 * @param modelArchitectures Model architectures from selected training runs
 * @param allTasks All available tasks from GraphQL
 * @param exactMatch If true, tasks must have exactly the same hardware keys as the model (default: false)
 * @returns Filtered tasks
 */
export const filterTasksForTrainingRuns = (
  modelArchitectures: Array<{
    id: string;
    name: string;
    inputHardwareConfig?: { config: any } | null;
    outputHardwareConfig?: { config: any } | null;
  }>,
  allTasks: Array<{
    id: string;
    name: string;
    hardwareConfig?: { config: any } | null;
  }>,
  exactMatch: boolean = false,
): {
  filteredTasks: Array<{
    id: string;
    name: string;
    hardwareConfig?: HardwareConfigSchema | null;
  }>;
} => {
  // Filter tasks based on hardware compatibility
  // Tasks with null hardware configs are always shown
  const filteredTasks = (() => {
    const allModelConfigs = modelArchitectures
      .flatMap((arch) => [
        arch.inputHardwareConfig?.config,
        arch.outputHardwareConfig?.config,
      ])
      .filter((config) => config != null);

    // If no hardware requirements, return all tasks
    if (allModelConfigs.length === 0) {
      return allTasks.map((task) => ({
        id: task.id,
        name: task.name,
        hardwareConfig:
          task.hardwareConfig?.config || task.hardwareConfig || null,
      }));
    }

    // Every key the architecture needs must be in the task's config, including
    // keys post-processing recovers — the task declares those in the config too
    // and lists them in `recoveredHardwareKeys`. Matches the server-side check
    // in createTrainingRunJob, down to dropping the keys that name no hardware.
    const modelArchitectureHardwareConfigKeys = excludeNonHardwareKeys(
      extractHardwareKeysFromConfig(allModelConfigs),
    );

    // Returned filtered tasks as flat map
    return allTasks
      .map((task) => ({
        id: task.id,
        name: task.name,
        hardwareConfig:
          task.hardwareConfig?.config || task.hardwareConfig || null,
      }))
      .filter((task) => {
        if (!task.hardwareConfig) {
          return true;
        }

        const taskKeys = extractHardwareKeysFromConfig(task.hardwareConfig);

        if (exactMatch) {
          // Strict validation: task must have EXACTLY the same hardware keys as
          // the model. Both sides are the full declared key set bar the keys
          // that name no hardware, which are already out of the model's list
          // and can never be in the task's — so dropping them is what makes
          // this a like-for-like comparison rather than one the task loses by
          // definition.
          const requiredKeys = [...modelArchitectureHardwareConfigKeys].sort();
          const taskKeysSorted = [...taskKeys].sort();

          return (
            requiredKeys.length === taskKeysSorted.length &&
            requiredKeys.every((key, index) => key === taskKeysSorted[index])
          );
        }

        // Loose validation: task must have AT LEAST the required hardware keys
        const validation = validateAllHardwareConfigsContainKeys(
          modelArchitectureHardwareConfigKeys,
          [taskKeys],
        );

        return validation.isValid;
      });
  })();

  return {
    filteredTasks,
  };
};

/**
 * Get a model from a model path using the available models from a GPU task runner state
 * @param taskState The GPU task runner state containing available models
 * @param modelPath The path to the model (e.g., "directory/filename")
 * @returns The found model or undefined if not found
 */
export const getModelFromPath = (
  taskState: { skillsModels?: any[] } | null | undefined,
  modelPath: string | null | undefined,
): ParsedModel | undefined => {
  if (!taskState?.skillsModels || !modelPath) {
    return undefined;
  }

  const model = taskState.skillsModels.find((availableModel) =>
    (availableModel.modelFilenames ?? []).find(
      (filename: string) =>
        `${availableModel.modelDirectory}/${filename}` === modelPath,
    ),
  );

  if (
    model &&
    model.modelMetadata?.trainingRun?.modelArchitecture?.inputHardwareConfig
      ?.config &&
    model.modelMetadata?.trainingRun?.modelArchitecture?.outputHardwareConfig
      ?.config
  ) {
    // Parse config fields if they are strings
    const inputConfig =
      model.modelMetadata.trainingRun.modelArchitecture.inputHardwareConfig
        .config;

    const outputConfig =
      model.modelMetadata.trainingRun.modelArchitecture.outputHardwareConfig
        .config;

    if (typeof inputConfig === 'string') {
      model.modelMetadata.trainingRun.modelArchitecture.inputHardwareConfig.config =
        JSON.parse(inputConfig);
    }

    if (typeof outputConfig === 'string') {
      model.modelMetadata.trainingRun.modelArchitecture.outputHardwareConfig.config =
        JSON.parse(outputConfig);
    }
  }

  return model;
};

/**
 * Creates a map of robot keys to robot IDs using the hardware config mapping.
 * @param hardwareConfigEquipmentMapping Mapping of hardware keys to equipment IDs
 * @param primaryRobotId ID of the primary robot
 * @param equipmentState State of the equipment
 * @returns Map of robot keys to robot IDs
 */
export const createRobotIdsMap = (
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping,
  primaryRobotId: string,
  equipmentState: EquipmentState,
): Record<string, string> => {
  const out = Object.entries(hardwareConfigEquipmentMapping).reduce(
    (acc, [robotKey, entry]) => {
      if (entry.robotEquipmentId == null) {
        if (robotKey !== 'robot_0') {
          log.error(
            'createRobotIdsMap',
            'Unknown convention: no robotEquipmentId assigned but robot key is not robot_0.',
            { robotKey, entry, hardwareConfigEquipmentMapping },
          );

          throw new Error(`Unexpected configuration.`);
        }

        acc[robotKey] = primaryRobotId;
      } else {
        const equipment = equipmentState.find(
          (device) => device.id === entry.robotEquipmentId,
        );

        if (equipment == null) {
          log.error(
            'createRobotIdsMap',
            'Failed to find equipment for robots entry.',
            { robotKey, entry, hardwareConfigEquipmentMapping },
          );

          throw new Error(
            `Failed to find equipment for robot key ${robotKey}.`,
          );
        }

        if (equipment.kind !== 'StandardBotsRO1') {
          log.error(
            'createRobotIdsMap',
            'Equipment is of wrong kind. Should be "StandardBotsRO1."',
            { robotKey, entry, hardwareConfigEquipmentMapping, equipment },
          );

          throw new Error(
            `Equipment assigned to robot key ${robotKey} is incorrect kind.`,
          );
        }

        const { botId } = equipment.state as StandardBotsRO1State;

        if (botId == null) {
          log.error('createRobotIdsMap', 'Equipment lacks a botId.', {
            robotKey,
            entry,
            hardwareConfigEquipmentMapping,
            equipment,
          });

          throw new Error(
            `Equipment assigned to robot key ${robotKey} lacks sufficient info.`,
          );
        }

        acc[robotKey] = botId;
      }

      return acc;
    },
    {} as Record<string, string>,
  );

  if (Object.keys(out).length === 0) {
    return { robot_0: primaryRobotId };
  }

  return out;
};
